
import {Component} from "vue-property-decorator";
import {mixins} from "vue-class-component";
import AuthMixin from "../../mixins/auth.mixin";
import {Api} from "shared/api";
import IconPicker from "@/components/icon-picker.vue";
import {apiHandler} from "@/services/api-handler";
import {IImportantLink} from "shared/entities/important-link";
import {CreateImportantLinkRequest, UpdateImportantLinkRequest} from "shared/api/routes/important-link";

type NewValues<T = keyof IImportantLink> = Record<string, Partial<IImportantLink>>;

@Component({
    components: {IconPicker}
})
export default class ManageImportantLinks extends mixins(AuthMixin) {
    importantLinks: IImportantLink[] = [];
    activeChanges: Record<string, Record<string, boolean>> = {};
    newValues: NewValues = {};
    newLink: CreateImportantLinkRequest|null = null;

    get orderedImportantLinks() {
        if (this.newLink) return [];
        return this.importantLinks.sort((a, b) => a.order - b.order);
    }

    trim(str: string) {
        if (str.length > 23) {
            return str.substring(0, 20) + "...";
        }
        return str;
    }

    async mounted() {
        await this.loadImportantLinks();
    }

    private async loadImportantLinks() {
        this.importantLinks = await apiHandler(() => Api.importantLink.Get());
        for (const importantLink of this.importantLinks) {
            this.activeChanges[importantLink.id] = {};
            this.newValues[importantLink.id] = {};
        }
    }

    async remove(importantLink: IImportantLink) {
        if (!confirm(`Are you sure you want to remove ${importantLink.title}`)) {
            return;
        }
        await apiHandler(() => Api.importantLink[importantLink.id].Delete());
        alert("Manual has been removed.");
        await this.loadImportantLinks();
    }

    canDecrease(importantLink: IImportantLink) {
        return importantLink.order > 1;
    }

    canIncrease(importantLink: IImportantLink) {
        return importantLink.order < this.importantLinks.reduce((o, bm) => bm.order > o ? bm.order : o, 0);
    }

    async decreaseOrder(importantLink: IImportantLink) {
        if (!this.canDecrease(importantLink)) return;
        const previous = this.orderedImportantLinks.filter(obm => obm.order === importantLink.order - 1);
        if (previous && previous.length >= 1) {
            await apiHandler(() => Api.importantLink[importantLink.id].Patch({order: ++previous[0].order}));
        }
        await Api.importantLink[importantLink.id].Patch({order: --importantLink.order});
    }

    async increaseOrder(importantLink: IImportantLink) {
        if (!this.canIncrease(importantLink)) return;
        const previous = this.orderedImportantLinks.filter(obm => obm.order === importantLink.order + 1);
        if (previous && previous.length >= 1) {
            await apiHandler(() => Api.importantLink[importantLink.id].Patch({order: --previous[0].order}));
        }
        await apiHandler(() => Api.importantLink[importantLink.id].Patch({order: ++importantLink.order}));
    }

    startFieldChange(importantLink: IImportantLink, field: keyof IImportantLink) {
        this.activeChanges[importantLink.id][field] = true;
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        this.newValues[importantLink.id][field] = importantLink[field];
        this.$forceUpdate();
    }

    endFieldChange(importantLink: IImportantLink, field: keyof IImportantLink) {
        this.activeChanges[importantLink.id][field] = false;
        this.$forceUpdate();
    }

    async changeIcon(importantLink: IImportantLink, icon: string) {
        const toChange = {icon};
        await apiHandler(() => Api.importantLink[importantLink.id].Patch(toChange));
        importantLink.icon = icon;
    }

    async changeField(importantLink: IImportantLink, field: keyof IImportantLink) {
        const toChange: Partial<IImportantLink> = {};
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        toChange[field] = this.newValues[importantLink.id][field];
        await apiHandler(() => Api.importantLink[importantLink.id].Patch(toChange));
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        importantLink[field] = this.newValues[importantLink.id][field];
        this.endFieldChange(importantLink, field);
    }

    startNew() {
        this.newLink = {
            title: "Google",
            description: "Sample Link",
            icon: "faLink",
            link: "https://google.com/",
            order: this.importantLinks.reduce((o, bm) => bm.order > o ? bm.order : o, 0) + 1
        };
    }

    async saveNew() {
        if (this.newLink == null) return;
        const newLinkSet = this.newLink;
        await apiHandler(() => Api.importantLink.Post(newLinkSet));
        this.newLink = null;
        await this.loadImportantLinks();
    }

    cancelNew() {
        this.newLink = null;
    }
}

